import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "container"
}
const _hoisted_2 = ["textContent"]
const _hoisted_3 = ["textContent"]
const _hoisted_4 = ["textContent"]
const _hoisted_5 = { class: "d-flex flex-column align-center" }
const _hoisted_6 = ["textContent"]
const _hoisted_7 = {
  key: 1,
  class: "text-left mx-auto mt-4",
  style: {"max-width":"500px"}
}
const _hoisted_8 = {
  key: 2,
  class: "d-flex align-center justify-center ga-4 mt-4"
}

import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { AeSdk, CompilerHttp, MemoryAccount } from "@aeternity/aepp-sdk";

import { SETTINGS } from "@/utils/constants";
import { Decimal } from "@/libs/decimal";
import { useAccounts } from "@/stores";
import {
  useAeppSdk,
  useInvitation,
  useNetwork,
  useCommunityFactory,
} from "@/composables";

import WalletConnectBtn from "@/components/WalletConnect/WalletConnectBtn.vue";
import AddressChip from "@/components/Common/AddressChip.vue";
import LivePriceFormatter from "@/components/Common/Pricing/LivePriceFormatter.vue";
import MessageBox from "@/components/Common/MessageBox.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'CollectInvitationLinkCard',
  setup(__props) {

const appName = SETTINGS.app.name;

const router = useRouter();
const route = useRoute();

const { getAeSdk, nodes } = useAeppSdk();
const { activeNetwork } = useNetwork();
const { activeAccount } = storeToRefs(useAccounts());
const { getAffiliationTreasury } = useCommunityFactory();
const { invitationCode } = useInvitation();

const invitationAmount = ref<Decimal>(Decimal.ZERO);
const invitationSender = ref();
const invitationClaimed = ref(false);

const loadingInvitation = ref(false);
const claimingReward = ref(false);

const errorMessage = ref();
const successMessage = ref();

const isRevoking = computed(
  () => invitationSender.value && invitationSender.value === activeAccount.value
);

watch(
  invitationCode,
  (invCode) => {
    if (invCode) {
      getInvitationRewardAmount();
    }
  },
  {
    immediate: true,
  }
);

watch(activeNetwork, () => {
  if (invitationCode.value) {
    getInvitationRewardAmount();
  }
});

/**
 * Retrieves the invitation reward amount for a given invitation code.
 * This function sets the loading state, initializes the AeSdk, retrieves the affiliation contract,
 * and updates the invitation details if the invitation code exists.
 * If the invitation has already been claimed, it displays an error message and closes the component after 3 seconds.
 * @throws {Error} If there is an error retrieving the invitation reward amount.
 */
async function getInvitationRewardAmount() {
  errorMessage.value = null;
  loadingInvitation.value = true;

  try {
    const account = new MemoryAccount(invitationCode.value);
    const sdk = new AeSdk({
      onCompiler: new CompilerHttp("https://v7.compiler.aepps.com"),
      nodes,
      accounts: [account],
    });
    sdk.selectNode(activeNetwork.value.name);

    const affiliationTreasury = await getAffiliationTreasury(sdk);

    const invitationCodes = await affiliationTreasury
      .invitationCodes()
      .catch(() => ({}));

    const invitation = invitationCodes.get(account.address);
    if (invitation) {
      invitationSender.value = invitation[0];
      invitationAmount.value = Decimal.fromBigNumberString(invitation[1]);
      invitationClaimed.value = invitation[2];

      if (invitationClaimed.value) {
        errorMessage.value = "This invitation has already been claimed.";
      }
    }
  } catch (error: any) {
    if (error?.message?.includes("Trying to call undefined function")) {
      errorMessage.value = "Please switch to the correct network.";
    } else {
      errorMessage.value = error?.message;
    }
  }
  loadingInvitation.value = false;
}

async function claimOrRevokeReward() {
  if (!invitationCode.value || !activeAccount.value) {
    return false;
  }
  claimingReward.value = true;
  const sdk = await getAeSdk();
  const affiliationTreasury = await getAffiliationTreasury(sdk);

  try {
    if (isRevoking.value) {
      const account = new MemoryAccount(invitationCode.value);
      await affiliationTreasury.revokeInvitationCode(account.address);
      successMessage.value = "Reward revoked successfully.";
    } else {
      await affiliationTreasury.redeemInvitationCode(
        invitationCode.value,
        activeAccount.value
      );
      successMessage.value = "Reward claimed successfully.";
    }
    invitationClaimed.value = true;
  } catch (error: any) {
    if (error?.message?.includes("INVITEE_ALREADY_REGISTERED")) {
      errorMessage.value =
        "Your account has already claimed an invite reward, try with another account.";
    } else {
      errorMessage.value = `Failed to claim reward. Reason: ${
        error?.message || "Unknown"
      }`;
    }
  }
  claimingReward.value = false;
}

function close() {
  localStorage.removeItem("invite_code");
  router.push({ query: { invite_code: null } });
  invitationCode.value = undefined;
  successMessage.value = null;
}

// Reset and hide the box if user claimed the reward and wants to navigate
watch(
  route,
  () => {
    if (successMessage.value) {
      invitationCode.value = undefined;
      successMessage.value = null;
    }
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  const _component_v_col = _resolveComponent("v-col")!
  const _component_i18n_t = _resolveComponent("i18n-t")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_fade_transition = _resolveComponent("v-fade-transition")!

  return (_openBlock(), _createBlock(_component_v_fade_transition, null, {
    default: _withCtx(() => [
      (!!_unref(invitationCode))
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_v_card, { class: "mb-2" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_row, { align: "center" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, {
                          cols: "12",
                          md: "6"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", {
                              class: "text-h3 pb-4 text-primary",
                              textContent: _toDisplayString(_ctx.$t('blocks.invitationCollect.header', { appName: _unref(appName) }))
                            }, null, 8, _hoisted_2),
                            _createElementVNode("p", {
                              class: "text-subtitle-1",
                              textContent: _toDisplayString(_ctx.$t('blocks.invitationCollect.desc1'))
                            }, null, 8, _hoisted_3),
                            _createElementVNode("p", {
                              class: "mt-4 text-body-2 opacity-60",
                              textContent: _toDisplayString(_ctx.$t('blocks.invitationCollect.desc2'))
                            }, null, 8, _hoisted_4)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, {
                          cols: "12",
                          md: "6",
                          class: "text-center"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_5, [
                              _createElementVNode("p", {
                                class: "text-h4",
                                textContent: _toDisplayString(
                    isRevoking.value
                      ? _ctx.$t('blocks.invitationCollect.revokeReward')
                      : _ctx.$t('blocks.invitationCollect.claimReward')
                  )
                              }, null, 8, _hoisted_6),
                              _createVNode(LivePriceFormatter, {
                                "ae-price": invitationAmount.value,
                                "watch-price": false,
                                "price-loading": loadingInvitation.value,
                                class: "text-h2 text-primary mt-2",
                                row: ""
                              }, null, 8, ["ae-price", "price-loading"])
                            ]),
                            (invitationSender.value && !isRevoking.value)
                              ? (_openBlock(), _createBlock(_component_i18n_t, {
                                  key: 0,
                                  keypath: "blocks.invitationCollect.addressInvited",
                                  class: "mt-4 d-flex ga-1 align-center justify-center flex-wrap",
                                  tag: "div"
                                }, {
                                  address: _withCtx(() => [
                                    _createVNode(AddressChip, { address: invitationSender.value }, null, 8, ["address"])
                                  ]),
                                  appName: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(appName)), 1)
                                  ]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            (errorMessage.value || successMessage.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                  (errorMessage.value)
                                    ? (_openBlock(), _createBlock(MessageBox, {
                                        key: 0,
                                        title: errorMessage.value,
                                        color: "error"
                                      }, null, 8, ["title"]))
                                    : (successMessage.value)
                                      ? (_openBlock(), _createBlock(MessageBox, {
                                          key: 1,
                                          title: _ctx.$t('common.success'),
                                          text: successMessage.value,
                                          color: "success"
                                        }, null, 8, ["title", "text"]))
                                      : _createCommentVNode("", true)
                                ]))
                              : _createCommentVNode("", true),
                            (!loadingInvitation.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                  (_unref(activeAccount) && !invitationClaimed.value)
                                    ? (_openBlock(), _createBlock(_component_v_btn, {
                                        key: 0,
                                        size: "large",
                                        color: "primary",
                                        loading: claimingReward.value,
                                        disabled: errorMessage.value,
                                        onClick: _cache[0] || (_cache[0] = ($event: any) => (claimOrRevokeReward()))
                                      }, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(isRevoking.value
                      ? _ctx.$t("blocks.invitationCollect.ctaRevoke")
                      : _ctx.$t("blocks.invitationCollect.ctaClaim")), 1)
                                        ]),
                                        _: 1
                                      }, 8, ["loading", "disabled"]))
                                    : _createCommentVNode("", true),
                                  _createVNode(WalletConnectBtn, {
                                    label: _ctx.$t('blocks.invitationCollect.connectWallet'),
                                    size: "large",
                                    "keep-label-on-sm": ""
                                  }, null, 8, ["label"]),
                                  (_unref(activeAccount))
                                    ? (_openBlock(), _createBlock(_component_v_btn, {
                                        key: 1,
                                        textContent: _toDisplayString(_ctx.$t('common.dismiss')),
                                        size: "large",
                                        color: "primary",
                                        variant: "outlined",
                                        onClick: _cache[1] || (_cache[1] = ($event: any) => (close()))
                                      }, null, 8, ["textContent"]))
                                    : _createCommentVNode("", true)
                                ]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})